import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getApiKey, setApiKey, getAppId, setAppId, removeCredentials } from '../services/sessionService';
import ApiKeyPrompt from './ApiKeyPrompt';
import Icon from './Icon';

const CredentialsContainer = styled.div`
  background-color: var(--paper);
  border-radius: var(--radius-md);
  padding: 16px;
`;

const CredentialsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: var(--ink-soft);
`;

const CredentialsTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-soft);
`;

const CredentialsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CredentialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`;

const CredentialCheck = styled.span`
  color: var(--diff-add);
  display: flex;
`;

const CredentialLabel = styled.span`
  color: var(--ink-faint);
`;

const CredentialValue = styled.span`
  color: var(--ink-soft);
  font-weight: 600;
  font-family: 'IBM Plex Mono', monospace;
`;

const EmptyState = styled.p`
  font-size: 13px;
  color: var(--ink-faint);
  margin: 0 0 12px 0;
`;

const Button = styled.button`
  width: 100%;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background-color var(--transition-fast), border-color var(--transition-fast);

  ${props => props.$variant === 'primary' ? `
    background-color: var(--ink);
    color: var(--white);
    border: 1.5px solid var(--ink);

    &:hover {
      background-color: var(--accent-ink);
      border-color: var(--accent-ink);
    }
  ` : `
    background-color: var(--diff-remove-bg);
    color: var(--diff-remove);
    border: 1px solid var(--diff-remove-bg);
    margin-top: 8px;

    &:hover {
      border-color: var(--diff-remove);
    }
  `}
`;

const ApiKeyManager = () => {
  const [apiKey, setApiKeyState] = useState(getApiKey());
  const [appId, setAppIdState] = useState(getAppId());
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const syncState = () => {
      setApiKeyState(getApiKey());
      setAppIdState(getAppId());
    };
    window.addEventListener('storage', syncState);
    return () => window.removeEventListener('storage', syncState);
  }, []);

  const handleSave = ({ newKey, newId }) => {
    setApiKey(newKey);
    setAppId(newId);
    setApiKeyState(newKey);
    setAppIdState(newId);
    setShowPopup(false);
  };

  const handleDelete = () => {
    removeCredentials();
    setApiKeyState(null);
    setAppIdState(null);
  };

  const hasCredentials = apiKey && appId;

  return (
    <CredentialsContainer>
      <CredentialsHeader>
        <Icon name="lock" size={14} />
        <CredentialsTitle>Credentials</CredentialsTitle>
      </CredentialsHeader>

      {hasCredentials ? (
        <CredentialsContent>
          <CredentialRow>
            <CredentialCheck><Icon name="check" size={14} /></CredentialCheck>
            <CredentialLabel>App ID:</CredentialLabel>
            <CredentialValue>{appId}</CredentialValue>
          </CredentialRow>
          <CredentialRow>
            <CredentialCheck><Icon name="check" size={14} /></CredentialCheck>
            <CredentialLabel>API Key:</CredentialLabel>
            <CredentialValue>{apiKey.slice(0, 4)}••••</CredentialValue>
          </CredentialRow>
          <Button onClick={handleDelete}>Remove</Button>
        </CredentialsContent>
      ) : (
        <>
          <EmptyState>No credentials configured</EmptyState>
          <Button $variant="primary" onClick={() => setShowPopup(true)}>
            Add Credentials
          </Button>
        </>
      )}

      {showPopup && <ApiKeyPrompt onSave={handleSave} onCancel={() => setShowPopup(false)} />}
    </CredentialsContainer>
  );
};

export default ApiKeyManager;